import { useState } from "react";
import { Button, Col, Form, Row, Spinner } from "react-bootstrap";
import QuillTextEditor from "../../../../../components/QuillTextEditor";
import MediaField from "../../../_components/MediaField";
import MemberRolesFields from "../../_components/MemberRolesFields";
import { buildMemberRoles, toCategoryKey } from "../../utils/category.utils";

const CategoryForm = ({
  form,
  validationErrors,
  submitting,
  submitLabel = "Create Category",
  submittingLabel = "Creating...",
  isEditMode = false,
  readOnly = false,
  onSubmit,
  onFieldChange,
  onBack,
}) => {
  const [keyTouched, setKeyTouched] = useState(isEditMode);

  const handleTitleChange = (value) => {
    onFieldChange("title", value);
    if (!isEditMode && !keyTouched) {
      onFieldChange("key", toCategoryKey(value));
    }
  };

  const handleKeyChange = (value) => {
    setKeyTouched(true);
    onFieldChange("key", value);
  };

  const handleMaxMembersChange = (rawValue) => {
    if (rawValue === "") {
      onFieldChange("max_members", "");
      return;
    }

    const parsed = Number(rawValue);
    if (!Number.isFinite(parsed)) return;

    const next = String(Math.max(1, Math.floor(parsed)));
    onFieldChange("max_members", next);
    onFieldChange(
      "member_roles",
      buildMemberRoles(next, form.member_roles || [])
    );
  };

  const disabled = submitting || readOnly;

  return (
    <Form onSubmit={onSubmit}>
      <h5 className="mb-3 mt-2">Basic Information</h5>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Title <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.title}
              onChange={(e) => handleTitleChange(e.target.value)}
              placeholder="Stock Prepaid"
              isInvalid={!!validationErrors.title}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.title}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={6}>
          <Form.Group className="mb-3">
            <Form.Label>
              Key <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={form.key}
              onChange={(e) => handleKeyChange(e.target.value)}
              placeholder="stock_prepaid"
              isInvalid={!!validationErrors.key}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.key}
            </Form.Control.Feedback>
            <Form.Text className="text-muted">
              Unique snake_case slug. Keep it stable — changing it can break
              related rallies, teams, and rankings.
            </Form.Text>
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={form.description}
              onChange={(e) => onFieldChange("description", e.target.value)}
              placeholder="Short description of this category"
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <Col md={12}>
          <Form.Group className="mb-3">
            <Form.Label>Consent form</Form.Label>
            <div className={disabled ? "opacity-75 pe-none" : ""}>
              <QuillTextEditor
                value={form.consent || ""}
                onChange={(next) => onFieldChange("consent", next)}
                placeholder="Write the consent form. Use {{driver_name}} for the driver name…"
                disabled={disabled}
              />
            </div>
            <Form.Text className="text-muted">
              Use {"{{driver_name}}"} where the driver&apos;s name should appear.
              Downloads are personalized per driver as a PDF with a signature
              block.
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>

      <h5 className="mb-3 mt-2">Rules</h5>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-3">
            <Form.Label>
              Max Members <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="number"
              min="1"
              step="1"
              value={form.max_members}
              onChange={(e) => handleMaxMembersChange(e.target.value)}
              isInvalid={!!validationErrors.max_members}
              disabled={disabled}
            />
            <Form.Control.Feedback type="invalid">
              {validationErrors.max_members}
            </Form.Control.Feedback>
          </Form.Group>
        </Col>

        <Col md={4} className="d-flex align-items-center">
          <Form.Group className="mb-3">
            <Form.Check
              type="switch"
              id="navigator_allowed"
              label="Navigator allowed"
              checked={Boolean(form.navigator_allowed)}
              onChange={(e) =>
                onFieldChange("navigator_allowed", e.target.checked)
              }
              disabled={disabled}
            />
          </Form.Group>
        </Col>

        <MemberRolesFields
          form={form}
          onFieldChange={onFieldChange}
          disabled={disabled}
        />
      </Row>

      <h5 className="mb-3 mt-2">Image</h5>
      <MediaField
        label="Category Image"
        value={form.image || ""}
        disabled={disabled}
        error={validationErrors.image}
        onChange={(next) => onFieldChange("image", next)}
      />

      <div className="d-flex justify-content-end gap-2 mt-3">
        <Button
          type="button"
          variant="outline-secondary"
          onClick={onBack}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button type="submit" disabled={disabled}>
          {submitting ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              {submittingLabel}
            </>
          ) : (
            submitLabel
          )}
        </Button>
      </div>
    </Form>
  );
};

export default CategoryForm;
